import z from 'zod';
import { ReportingInputFileTypes, ReportingTaskStatusCodes, ReportingTaskTypes, SurveyImportJobStatus } from './enums';
import { paginated } from './utils';
import { isoDateTimeSchema, numberSchema, stringSchema, taskNameSchema, uuidSchema } from './validation-utils';
/**
 * ENTITIES
 */
export const ReportingFile = z
    .object({
    filename: z.string(),
    downloadUrl: z.string(),
    expiresAt: isoDateTimeSchema,
})
    .readonly();
export const ReportingInputFileType = z
    .object({
    id: ReportingInputFileTypes,
    displayName: z.string(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .readonly();
export const ReportingTaskConfig = z
    .object({
    separateByAttr: z.string().nullable(),
    commentFields: z.array(z.string()).nullable(),
    crossConditions: z.array(z.array(z.string())).nullable(),
})
    .readonly();
export const ReportingTaskStatusCode = z
    .object({
    id: ReportingTaskStatusCodes,
    displayName: z.string(),
})
    .readonly();
export const ReportingTaskStatusInfo = z
    .object({
    artifacts: z
        .array(z
        .object({
        type: z.string(),
        bucket: z.string(),
        key: z.string(),
        filename: z.string(),
    })
        .readonly())
        .readonly(),
    errors: z.array(z.unknown()),
})
    .readonly();
// Rails: TaskStatus model (task_status.rb). Serialized from _task.json.jbuilder:6-11.
// NOTE: Rails response also included code_i10n (I18n translation); we omit that here.
export const ReportingTaskStatus = z
    .object({
    code: ReportingTaskStatusCode,
    errors: z.array(z.unknown()).optional(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .readonly();
export const ReportingTaskType = z
    .object({
    id: ReportingTaskTypes,
    displayName: z.string(),
})
    .readonly();
export const ReportingWorkflowInfo = z
    .object({
    api_endpoint_stage_1: z.string().optional(),
})
    .readonly();
// Rails: Workflow model (workflow.rb). Serialized from views/workflows/_workflow.json.jbuilder.
export const ReportingWorkflow = z
    .object({
    uuid: uuidSchema,
    name: z.string().nullable(),
    info: ReportingWorkflowInfo.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .readonly();
// Rails: Task model (task.rb). Serialized from views/tasks/_task.json.jbuilder.
// NOTE: Rails response also included etl_task_info inline on every task; we expose that only via triggerEtl() in the service.
// NOTE: Rails ordered statuses asc; we order desc in the repository (newest first). Reverse if needed.
// NOTE: integer id is intentionally omitted — uuid is the public identifier.
export const ReportingTask = z
    .object({
    uuid: uuidSchema,
    name: z.string().nullable(),
    userName: z.string().nullable(),
    organization: z
        .object({
        uuid: uuidSchema,
        name: z.string(),
    })
        .readonly()
        .nullable(),
    type: ReportingTaskType,
    surveyProjectId: uuidSchema.nullable(),
    /**
     * The survey the task's project points at. Null when the task has no
     * project, or when the project it referenced has since been deleted.
     */
    survey: z
        .object({
        surveyId: uuidSchema,
        name: z.string(),
        /** ISO timestamp; the survey's target date, or its creation date when unset. */
        surveyDate: isoDateTimeSchema,
    })
        .readonly()
        .nullable(),
    workflow: ReportingWorkflow,
    config: ReportingTaskConfig.nullable(),
    inputFiles: z.array(ReportingFile),
    artifacts: z.array(ReportingFile),
    status: ReportingTaskStatus.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .readonly();
/**
 * REQUEST VALIDATION SCHEMAS
 */
// Rails: TasksController#index (tasks_controller.rb:6-13). Rails used Ransack q[name_or_user_name_i_cont] for search and Kaminari p/per for pagination.
export const GetReportingTaskListRequestQuery = z.object({
    from: numberSchema.default(0),
    limit: numberSchema.max(100).default(25),
    search: stringSchema.optional(),
});
// Rails: tasks_controller.rb:39, 52 — Task.find(params[:id] or params[:task_id])
// NOTE: Rails used integer IDs; we use UUIDs to avoid exposing auto-increment IDs to API consumers.
export const GetReportingTaskRequestParams = z.object({
    taskUuid: uuidSchema,
});
const ReportingTaskSurveySchema = z.discriminatedUnion('taskTypeId', [
    z.object({
        taskTypeId: z.union([
            z.literal(ReportingTaskTypes.enum.NEW_SURVEY),
            z.literal(ReportingTaskTypes.enum.ETL_ONLY),
        ]),
    }),
    z.object({
        taskTypeId: z.literal(ReportingTaskTypes.enum.OVERWRITE_SURVEY),
        surveyProjectId: uuidSchema,
    }),
]);
// Rails: TasksController#create (tasks_controller.rb:16-28) and task_params (tasks_controller.rb:55-64).
// NOTE: Rails accepted input_files as Active Storage file uploads; we accept pre-uploaded S3 references instead.
// NOTE: Rails parsed config from a JSON string (JSON.parse(t_params[:config] || '{}')); we accept it as a structured object.
export const CreateReportingTaskRequestBody = z.object({
    organizationId: uuidSchema.nullish(),
    name: taskNameSchema,
    taskSurvey: ReportingTaskSurveySchema,
    inputFiles: z
        .array(z.object({
        typeId: ReportingInputFileTypes,
        s3Key: stringSchema.nonempty(),
        filename: stringSchema.nonempty(),
        contentType: stringSchema.nonempty(),
        byteSize: z.number().int().positive(),
        checksum: stringSchema.nonempty(),
    }))
        .min(1)
        .max(10),
    separateByAttr: stringSchema.default(''),
    crossConditions: z.array(z.array(stringSchema)),
});
// Rails: TaskStatusesController#create (task_statuses_controller.rb:4-11). The ETL Lambda POSTs back to this endpoint.
// Presence of errors array determines ETL_ERROR vs ETL_SUCCESS (task_statuses_controller.rb:7).
export const ReportingEtlCallbackRequestBody = z.object({
    errors: z.array(z.unknown()).optional(),
    artifacts: z
        .array(z.object({
        // Leaving the output file type (set by the ETL) as a loose string
        // for now since we don't have any use cases for it.
        type: z.string(),
        bucket: z.string(),
        key: z.string(),
        filename: z.string(),
    }))
        .optional(),
    request: z.object({
        task: z.object({
            uuid: uuidSchema,
        }),
    }),
});
export const PrepareReportingInputFileUploadRequestBody = z.object({
    contentTypes: z.array(stringSchema.nonempty()).min(1).max(10),
});
/**
 * Request body for triggering the async rawdata import pipeline from a reporting
 * task. `targetDate` is a `YYYY-MM` string; omit or leave empty for no target
 * date.
 */
export const TriggerReportingTaskImportRequestBody = z.object({
    targetDate: stringSchema.optional(),
});
/**
 * API RESPONSE TYPES
 */
// Rails: TasksController#index → views/tasks/index.json.jbuilder
export const GetReportingTaskListResponse = paginated(ReportingTask);
// Rails: TasksController#show → views/tasks/show.json.jbuilder
export const GetReportingTaskResponse = ReportingTask;
// Rails: TasksController#create → views/tasks/show.json.jbuilder (renders show on create)
export const CreateReportingTaskResponse = ReportingTask;
// Rails: TasksController#run_etl (tasks_controller.rb:38-47) — Rails returned the full task JSON + api_endpoint; we return a simple ack.
export const RunReportingEtlResponse = z.object({ triggered: z.literal(true) }).readonly();
/**
 * Response for the reporting-task import trigger: the job id and the ids of the
 * placeholder survey / survey project, plus the published EventBridge event id.
 */
export const TriggerReportingTaskImportResponse = z
    .object({
    jobId: uuidSchema,
    surveyId: uuidSchema,
    surveyProjectId: uuidSchema,
    eventId: z.string(),
})
    .readonly();
/** Latest import pipeline job for a reporting task, surfaced on the task detail view. */
export const ReportingTaskImportJob = z
    .object({
    jobId: uuidSchema,
    status: SurveyImportJobStatus,
    startedAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .readonly();
/**
 * Response for the reporting-task import status. `job` is null when the task has
 * never been imported.
 */
export const GetReportingTaskImportResponse = z.object({ job: ReportingTaskImportJob.nullable() }).readonly();
// Rails: TaskStatusesController#create → views/task_statuses/create.json.jbuilder (renders full task partial)
export const ReportingEtlCallbackResponse = ReportingTask;
export const GetReportingTaskStatusesResponse = z.array(ReportingTaskStatus);
export const GetReportingTaskInputFilesResponse = z.array(ReportingFile);
export const GetReportingTaskArtifactsResponse = z.array(ReportingFile);
export const PrepareReportingInputFileUploadResponse = z.array(z
    .object({
    s3Key: z.string(),
    uploadUrl: z.string(),
    expiresAt: isoDateTimeSchema,
})
    .readonly());
export const GetReportingInputFileTypeListResponse = z.array(ReportingInputFileType);
export const GetReportingTaskTypeListResponse = z.array(ReportingTaskType);
//# sourceMappingURL=reporting.js.map